/**
 * Column type map for raw master-data tables.
 *
 * Mirrored (intentionally, no cross-package import) from
 * `tools/lib/table-schema.ts` so the admin UI can pick the right inline-edit
 * widget per column without depending on the CSV/seeding tooling.
 *
 * If a new column / table is added at the DB level, update this map AND
 * `tools/lib/table-schema.ts` together.
 *
 * NOTE: wave-enemy 의 `*_multiplier` 컬럼은 DB 상 BIGINT
 * (20260722014447_multiplier_columns_bigint.sql). 여기서는 계속 'integer' 로 둔다 —
 * 'integer' 는 정수 입력 위젯(step=1, Number.parseInt) 선택용이지 int32 범위
 * 제약이 아니며, 값은 JS safe integer 안쪽이다.
 */
export type ColumnType =
  | 'text'
  | 'integer'
  | 'numeric'
  | 'boolean'
  | 'jsonb'
  | 'integer[]'
  | 'image';

export const TABLE_COLUMNS: Record<string, Record<string, ColumnType>> = {
  world: { id: 'integer', name: 'text', bg: 'text' },
  enemy: {
    id: 'integer',
    name: 'text',
    resource_id: 'text',
    health: 'integer',
    speed: 'integer',
    atk: 'integer',
    score: 'integer',
    movement_pattern: 'jsonb',
    attack_patterns: 'jsonb',
    compound_type: 'text',
    slow_resistance: 'integer',
    knockback_resistance: 'integer',
  },
  map: {
    id: 'text',
    play_area_shape: 'text',
    play_area_size_x: 'numeric',
    play_area_size_y: 'numeric',
    sprite_scale: 'numeric',
    platform_sprite: 'text',
  },
  character: {
    id: 'integer',
    name: 'text',
    resource_id: 'text',
    cost: 'jsonb',
    stats: 'jsonb',
    skills: 'jsonb',
  },
  skin: {
    id: 'integer',
    character_id: 'integer',
    name: 'text',
    description: 'text',
    stats: 'jsonb',
    skills: 'jsonb',
    cost: 'jsonb',
    acquire_guide: 'text',
    acquire_badge: 'text',
    resource_id: 'text',
  },
  consumable_item: {
    id: 'text',
    name: 'text',
    resource_id: 'text',
    usage_limit: 'text',
    is_active: 'boolean',
    description: 'text',
  },
  game_config: {
    id: 'integer',
    // Grouped tunable jsonb columns
    score_config: 'jsonb',
    ticket_configs: 'jsonb',
    wave_shop_config: 'jsonb',
    pet_golden_summon_config: 'jsonb',
    guest_config: 'jsonb',
    valhalla_sweep_config: 'jsonb',
    degen_config: 'jsonb',
    stage_bonus_config: 'jsonb',
    // Other server-read configs
    ad_tickets_daily_limit: 'jsonb',
    tx_tickets_daily_limit: 'jsonb',
    initial_assets: 'jsonb',
    invites: 'jsonb',
    pwa_reward: 'jsonb',
    // Client-read configs
    pet_exp_per_sapphire_amount: 'integer',
    mastery_first_play_stage: 'integer',
    nav_unlock_stages: 'jsonb',
    league_ending_soon_threshold: 'jsonb',
  },
  game_difficulty: {
    id: 'text',
    level: 'integer',
    score_multiplier_f: 'numeric',
    difficulty_effect: 'text',
    effect_target: 'text',
    value: 'numeric',
    description: 'text',
  },
  meta_tag: { id: 'integer', name: 'text', icon: 'text', max_level: 'integer' },
  meta_tag_synergy: {
    meta_tag_id: 'integer',
    level: 'integer',
    description: 'text',
    synergy_stats: 'jsonb',
    synergy_skills: 'jsonb',
  },
  item: {
    id: 'integer',
    name: 'text',
    category: 'text',
    icon: 'text',
    appearance_resource_id: 'text',
    launcher_type: 'text',
    description: 'text',
    grade: 'integer',
    stats: 'jsonb',
    skills: 'jsonb',
    meta_tag_ids: 'integer[]',
    summon_pet_ids: 'integer[]',
  },
  pet_grade: {
    grade: 'integer',
    name: 'text',
    description: 'text',
    return_sapphire_amount: 'integer',
    upgrade_sapphire_amount: 'integer',
  },
  pet: {
    id: 'integer',
    name: 'text',
    resource_id: 'text',
    description: 'text',
    max_level: 'integer',
    grade: 'integer',
    meta_tag_ids: 'integer[]',
  },
  pet_upgrade: {
    pet_id: 'integer',
    level: 'integer',
    description: 'text',
    stats: 'jsonb',
    skills: 'jsonb',
    character_buff_stats: 'jsonb',
    character_buff_skills: 'jsonb',
    required_exp: 'integer',
  },
  pet_summon_config: {
    id: 'text',
    summon_type: 'text',
    summon_count: 'integer',
    cost: 'jsonb',
  },
  pet_summon_level: {
    level: 'integer',
    summon_count: 'integer',
    minimum_grade: 'integer',
    guarantee_count: 'integer',
    golden_grade: 'integer',
    golden_guarantee_count: 'integer',
  },
  pet_summon_probability: {
    level: 'integer',
    summon_type: 'text',
    pet_id: 'integer',
    weight: 'integer',
  },
  league_table: {
    id: 'integer',
    name: 'text',
    total_wave_count: 'integer',
    meta_tags: 'integer[]',
    bg: 'text',
    map_id: 'text',
    wave_preview_enabled: 'boolean',
  },
  league_table_wave: {
    league_table_id: 'integer',
    wave_number: 'integer',
    kill_goal: 'integer',
    time_limit_seconds: 'integer',
    silver_reward: 'integer',
    shop_config_id: 'integer',
    max_alive_enemies: 'integer',
  },
  league_table_wave_enemy: {
    league_table_id: 'integer',
    wave_number: 'integer',
    enemy_id: 'integer',
    min_health_multiplier: 'integer',
    max_health_multiplier: 'integer',
    min_speed_multiplier: 'integer',
    max_speed_multiplier: 'integer',
    min_atk_multiplier: 'integer',
    max_atk_multiplier: 'integer',
    min_spawn_interval: 'integer',
    max_spawn_interval: 'integer',
    spawn_pattern: 'jsonb',
    slow_resistance: 'numeric',
    knockback_resistance: 'numeric',
  },
  league_season: {
    id: 'integer',
    name: 'text',
    season_type: 'text',
    start_time: 'text',
    end_time: 'text',
    league_table_id: 'integer',
    reward_table_id: 'text',
    // 리그 보상 web2/web3 분리 — 비어 있으면 reward_table_id 로 폴백.
    reward_table_id_web2: 'text',
    reward_table_id_web3: 'text',
    total_prize_ncg: 'numeric',
    retrieve_cost: 'jsonb',
  },
  valhalla: {
    id: 'integer',
    name: 'text',
    table_id: 'integer',
    start_time: 'text',
    end_time: 'text',
    total_wave_count: 'integer',
    retrieve_cost: 'jsonb',
    meta_tags: 'integer[]',
    bg: 'text',
    map_id: 'text',
    sprite_scale: 'numeric',
    wave_preview_enabled: 'boolean',
    unlock_stage_id: 'integer',
  },
  valhalla_season: {
    id: 'integer',
    name: 'text',
    start_time: 'text',
    end_time: 'text',
    valhalla_table_id: 'integer',
  },
  valhalla_wave: {
    valhalla_id: 'integer',
    wave_number: 'integer',
    kill_goal: 'integer',
    time_limit_seconds: 'integer',
    silver_reward: 'integer',
    shop_config_id: 'integer',
    bg: 'text',
    reward: 'jsonb',
    max_alive_enemies: 'integer',
  },
  valhalla_wave_enemy: {
    valhalla_id: 'integer',
    wave_number: 'integer',
    enemy_id: 'integer',
    health_multiplier: 'integer',
    speed_multiplier: 'integer',
    atk_multiplier: 'integer',
    spawn_interval: 'integer',
    spawn_pattern: 'jsonb',
  },
  degen_league: {
    id: 'integer',
    name: 'text',
    meta_tags: 'integer[]',
    bg: 'text',
    map_id: 'text',
    reward_table_id: 'text',
  },
  degen_league_wave: {
    degen_league_id: 'integer',
    wave_number: 'integer',
    kill_goal: 'integer',
    time_limit_seconds: 'integer',
    silver_reward: 'integer',
    shop_config_id: 'integer',
    max_alive_enemies: 'integer',
  },
  degen_league_wave_enemy: {
    degen_league_id: 'integer',
    wave_number: 'integer',
    enemy_id: 'integer',
    min_health_multiplier: 'integer',
    max_health_multiplier: 'integer',
    min_speed_multiplier: 'integer',
    max_speed_multiplier: 'integer',
    min_atk_multiplier: 'integer',
    max_atk_multiplier: 'integer',
    min_spawn_interval: 'integer',
    max_spawn_interval: 'integer',
    spawn_pattern: 'jsonb',
  },
  degen_rune_pool: {
    id: 'text',
    stat: 'text',
    direction: 'text',
    target: 'text',
    value: 'integer',
    weight: 'integer',
    selection_weight: 'integer',
  },
  degen_conditional_pool: {
    id: 'text',
    condition_key: 'text',
    condition_fallback: 'text',
    bonus_pct: 'integer',
    difficulty: 'text',
    tracker_type: 'text',
    params: 'jsonb',
    selection_weight: 'integer',
  },
  degen_conditional_count_prob: { difficulty: 'text', probabilities: 'jsonb' },
  degen_season: {
    id: 'integer',
    name: 'text',
    start_time: 'text',
    end_time: 'text',
    norns_table_id: 'integer',
    jotnar_table_id: 'integer',
    ragnarok_table_id: 'integer',
    // 시즌 → 보상표 참조(느슨한 FK) — 20260908120000_degen_season_reward.sql
    reward_table_id: 'text',
    // 페이즈 경계(분) — 20260709121239_degen_season_phase_prize_fields_dev.sql
    warmup_duration_min: 'integer',
    compete_duration_min: 'integer',
    boost_duration_min: 'integer',
    claim_duration_min: 'integer',
    // 상금풀 — prize_pool_currency 가 어느 금액 쌍을 읽을지 결정한다('ncg' | 'glory').
    // 20260907043000_degen_prize_pool_glory.sql
    prize_pool_base_ncg: 'numeric',
    prize_pool_ncg_per_ticket: 'numeric',
    prize_pool_currency: 'text',
    prize_pool_base_glory: 'numeric',
    prize_pool_glory_per_ticket: 'numeric',
  },
  quest: {
    id: 'integer',
    title: 'text',
    description: 'text',
    quest_type: 'text',
    clear_condition: 'jsonb',
    rewards: 'jsonb',
    is_active: 'boolean',
    order: 'integer',
    is_event: 'boolean',
  },
  quest_milestone: {
    id: 'integer',
    quest_type: 'text',
    required_count: 'integer',
    rewards: 'jsonb',
  },
  skill: { skill_type: 'text', level: 'integer', skill_config: 'jsonb' },
  stage: {
    id: 'integer',
    world_id: 'integer',
    bg: 'text',
    name: 'text',
    icon: 'text',
    total_waves: 'integer',
    recommend_combat_point: 'integer',
    stage_reward: 'jsonb',
    retrieve_cost: 'jsonb',
    map_id: 'text',
    meta_tags: 'integer[]',
    star_thresholds: 'jsonb',
    wave_preview_enabled: 'boolean',
  },
  stage_wave: {
    stage_id: 'integer',
    wave_number: 'integer',
    kill_goal: 'integer',
    time_limit_seconds: 'integer',
    silver_reward: 'integer',
    shop_config_id: 'integer',
    max_alive_enemies: 'integer',
  },
  stage_wave_enemy: {
    stage_id: 'integer',
    wave_number: 'integer',
    enemy_id: 'integer',
    health_multiplier: 'integer',
    speed_multiplier: 'integer',
    atk_multiplier: 'integer',
    spawn_interval: 'integer',
    spawn_pattern: 'jsonb',
    slow_resistance: 'numeric',
    knockback_resistance: 'numeric',
  },
  upgrade_config: {
    id: 'integer',
    title: 'text',
    order: 'integer',
    max_level: 'integer',
    description: 'text',
    icon: 'text',
    stat_increments: 'jsonb',
    costs: 'jsonb',
    golden_costs: 'jsonb',
    ruby_costs: 'jsonb',
    emerald_costs: 'jsonb',
  },
  wave_shop_config: {
    id: 'integer',
    item_count: 'integer',
    consumable_item_count: 'integer',
  },
  item_group: {
    wave_shop_config_id: 'integer',
    item_id: 'integer',
    silver_price_min: 'integer',
    silver_price_max: 'integer',
    weight: 'integer',
  },
  guide_quest: {
    id: 'integer',
    title: 'text',
    guide_text: 'text',
    clear_condition: 'jsonb',
    rewards: 'jsonb',
  },
  attendance_reward: { id: 'integer', static: 'jsonb', repeat: 'jsonb' },
  event_attendance: { id: 'integer', static: 'jsonb', repeat: 'jsonb', is_active: 'boolean' },
  league_reward: {
    reward_table_id: 'text',
    min_rank: 'integer',
    max_rank: 'integer',
    rewards: 'jsonb',
  },
  product: {
    product_id: 'text',
    name: 'text',
    description: 'text',
    price: 'integer',
    original_price: 'integer',
    display_style: 'text',
    stock: 'integer',
    is_active: 'boolean',
    availability: 'text',
    category: 'text',
    image_url: 'image',
    delivery_items: 'jsonb',
    icon: 'text',
    resource_id: 'text',
    name_key: 'text',
    description_key: 'text',
    pass_track: 'jsonb',
    pass_group: 'text',
    entitlements: 'jsonb',
    entitlement_gen: 'integer',
    duration_days: 'integer',
    lifetime_limit: 'integer',
    period_limit: 'text',
    sale_start: 'text',
    sale_end: 'text',
  },
  entitlement: { type: 'text', value: 'numeric' },
  // 결제 이벤트 v2 — tiers(threshold_vx/rewards 배열)는 JSON 편집기 셀로 통째 편집.
  // 운영상 active=true 는 항상 1개 행만 유지 (편집기 상단 안내 참고).
  payment_event: {
    id: 'integer',
    active: 'boolean',
    start_at: 'integer',
    end_at: 'integer',
    grace_days: 'integer',
    title_key: 'text',
    tiers: 'jsonb',
  },
  // 글로리 샵 상품 — cost={"GLORY":n}, delivery_items={RewardKey:qty} 는 jsonb 편집기 셀로,
  // is_active 는 boolean 위젯으로 편집해야 한다(텍스트로 폴백되면 "false" 문자열이 truthy 로
  // 접혀 비활성 상품이 노출된다). tools/lib/table-schema.ts 의 정의와 컬럼·타입이 1:1 이다.
  glory_shop_item: {
    id: 'text',
    name_key: 'text',
    category: 'text',
    display_order: 'integer',
    cost: 'jsonb',
    delivery_items: 'jsonb',
    purchase_limit: 'integer',
    period_limit: 'text',
    start_at: 'integer',
    end_at: 'integer',
    sale_season_id: 'integer',
    icon: 'text',
    is_active: 'boolean',
    gacha_box_id: 'text',
    gacha_ten_cost: 'integer',
  },
  free_shop_item: {
    id: 'text',
    acquire: 'text',
    category: 'text',
    name_key: 'text',
    description_key: 'text',
    icon: 'text',
    display_order: 'integer',
    delivery_items: 'jsonb',
    purchase_limit: 'integer',
    period_limit: 'text',
    start_at: 'integer',
    end_at: 'integer',
    availability: 'text',
    is_active: 'boolean',
  },
  // 글로리 뽑기 확률표 엔트리. weight/delivery_items 는 §4.4 fail-closed 검증 대상이므로
  // authoring 오타가 그대로 DB CHECK 를 통과하지 않게 jsonb/integer 위젯으로 편집한다.
  glory_gacha_entry: {
    id: 'text',
    box_id: 'text',
    weight: 'integer',
    delivery_items: 'jsonb',
    name_key: 'text',
    icon: 'text',
    rarity: 'text',
    display_order: 'integer',
    is_active: 'boolean',
  },
  folkvangr_boss: {
    id: 'integer',
    name_key: 'text',
    resource_id: 'text',
    bg: 'text',
    blessed_meta_tag_id: 'integer',
    blessed_damage_mul: 'numeric',
    blessed_hp_mul: 'numeric',
    duration_sec: 'integer',
    score_mul: 'numeric',
  },
  folkvangr_attack_pattern: {
    id: 'integer',
    name: 'text',
    type: 'text',
    params: 'jsonb',
  },
  folkvangr_boss_pattern: {
    boss_id: 'integer',
    at_sec: 'numeric',
    pattern_id: 'integer',
    damage: 'numeric',
    repeat_every_sec: 'numeric',
    repeat_count: 'integer',
  },
  folkvangr_grade_stat: {
    grade: 'integer',
    base_dps: 'numeric',
    base_hp: 'numeric',
    level_dps_k: 'numeric',
    level_hp_k: 'numeric',
    bt_dps_k: 'numeric',
    bt_hp_k: 'numeric',
  },
  folkvangr_omen: {
    id: 'integer',
    rune_pool_id: 'integer',
    name_key: 'text',
    description_key: 'text',
    icon: 'text',
    effects: 'jsonb',
    selection_weight: 'integer',
    is_active: 'boolean',
  },
  folkvangr_season: {
    id: 'integer',
    name: 'text',
    start_time: 'text',
    end_time: 'text',
    rune_pool_id: 'integer',
    reward_table_id: 'text',
    // 시즌 하나가 곧 하나의 편성이다 — 매일 다른 보스가 필요하면 하루짜리 시즌을
    // 이어 붙인다. omen_id 를 비우면 그 시즌은 계시 없이 보스만 남고,
    // blessed_meta_tag_id 를 비우면 보스 행의 축복을 쓴다.
    boss_id: 'integer',
    omen_id: 'integer',
    blessed_meta_tag_id: 'integer',
    play_reward: 'jsonb',
    daily_free_count: 'integer',
    daily_ad_count: 'integer',
    daily_ticket_max: 'integer',
    probe_sample_count: 'integer',
    probe_density: 'numeric',
    reset_hour_utc: 'integer',
  },
  folkvangr_reward: {
    reward_table_id: 'text',
    tier_order: 'integer',
    rank_type: 'text',
    rank_from: 'numeric',
    rank_to: 'numeric',
    rewards: 'jsonb',
  },
  degen_season_reward: {
    reward_table_id: 'text',
    tier_order: 'integer',
    rank_from: 'integer',
    rank_to: 'integer',
    prize_pct: 'numeric',
    rewards: 'jsonb',
  },
};

export function getColumnType(table: string, column: string): ColumnType {
  const t = TABLE_COLUMNS[table];
  if (!t) return 'text';
  return t[column] ?? 'text';
}

/**
 * Ordered editable column list for a table. Falls back to row keys if the
 * table is unknown to this map (e.g. a new DB migration the UI hasn't caught
 * up with yet). Always excludes `updated_at`, which is auto-managed.
 */
export function getEditableColumns(table: string, fallbackRow?: Record<string, unknown>): string[] {
  const known = TABLE_COLUMNS[table];
  if (known) return Object.keys(known);
  if (!fallbackRow) return [];
  return Object.keys(fallbackRow).filter((k) => k !== 'updated_at');
}
